import { useMemo, useRef, useState } from 'react';
import Header from '../../components/Header/Header';
import './PaymentProcessor.scss';
import { LuMoreVertical } from 'react-icons/lu';
import Dropdown from '../../components/Dropdown/Dropdown';
import { formatToNaira, useOutsideClick } from '../../helpers/utils';
import { Loader, Switch } from '@piggytechng/component-library';
import ModalUpdateMinimumBalanceThreshold from '../../components/Modals/ModalUpdateMinimumBalanceThreshold';
import ModalSwitchProcessorBeneficiaryBank from '../../components/Modals/ModalSwitchProcessorBeneficiaryBank';
import { PiBank, PiShieldWarningLight } from 'react-icons/pi';
import ModalToggleProcessorStatus from '../../components/Modals/ModalToggleProcessorStatus';
import ModalViewProcessorBanks from '../../components/Modals/ModalViewProcessorBanks';
import dayjs from 'dayjs';
import useProcessor from '../../hooks/useProcessor';

const PaymentProcessor = () => {
  const [ppDetails, setPPDetails] = useState({});
  const [showDropdown, setShowDropdown] = useState({
    index: -1,
    show: false,
  });

  const dropdownRef = useRef(null);
  const actionRef = useRef(null);
  useOutsideClick(dropdownRef, setShowDropdown, actionRef);
  const [minimumBalanceModal, setMinimumBalanceModal] = useState(false);
  const [processorBanksModal, setProcessorBanksModal] = useState(false);
  const [ModalToggleProcessorStatusModal, setModalToggleProcessorStatusModal] = useState(false);
  const [viewProcessorBanksModal, setViewProcessorBanksModal] = useState(false);

  const { Banks, Processors, isLoadingProcessors } = useProcessor();

  const processorData = useMemo(() => {
    if (!Processors) return [];

    return Processors.allProvidersInfo.map((provider: any) => {
      return {
        provider: provider.provider,
        status: provider.configuration.isEnabled,
        beneficiaryBanks: provider.assignedBanks,
        currentBalance: provider.balanceInfo.retreivedBalanceSuccessfully
          ? formatToNaira(provider.balanceInfo.balanceInKobo / 100)
          : 'N/A',
        minimumBalanceAlertThreshold: provider.configuration.minimumBalanceThreshold
          ? formatToNaira(provider.configuration.minimumBalanceThreshold / 100)
          : 'N/A',
        lastEnabledAt: provider.configuration.lastEnabledAt
          ? dayjs(provider.configuration.lastEnabledAt).format('MMM YYYY, h:mm A')
          : '--',
        lastDisabledAt: provider.configuration.lastDisabledAt
          ? dayjs(provider.configuration.lastDisabledAt).format('D MMM YYYY, h:mm A')
          : '',
      };
    });
  }, [Processors, Banks]);

  const dropdownArray = [
    {
      icon: <PiBank />,
      text: 'Manage Processor Banks',
      fn: () => setProcessorBanksModal(true),
    },
    {
      icon: <PiShieldWarningLight />,
      text: 'Update Minimum Balance Threshold',
      fn: () => setMinimumBalanceModal(true),
    },
  ];

  const handleShowDropdown = (e: React.MouseEvent<HTMLButtonElement, MouseEvent>, i: number) => {
    e.stopPropagation();
    if (i === showDropdown.index) {
      setShowDropdown({ ...showDropdown, show: false, index: -1 });
    } else {
      setShowDropdown({ ...showDropdown, show: true, index: i });
    }

    setPPDetails(processorData[i]);
  };

  return (
    <>
      <Header name="Payment Processor" />

      <div className="payment-processor">
        {isLoadingProcessors ? (
          <div className="page-loader">
            <Loader color="#0F60D6" />
          </div>
        ) : (
          <>
            <div className="payment-processor__body">
              <div className="table-container  payment-processor">
                <table className="table">
                  <thead className="table_head">
                    <tr>
                      <th className="enable">Status</th>
                      <th className="processorName">Provider</th>
                      <th className="beneficiaryBank"> Banks</th>
                      <th className="currentBalance">Current Balance</th>
                      <th className="minimumBalanceThrehold">Min Balance Threshold</th>
                      <th className="lastUpdated">Last Enabled At</th>
                      <th className="action"></th>
                    </tr>
                  </thead>

                  <tbody className="table-body">
                    {processorData?.map((processor: any, i: number) => {
                      return (
                        <tr key={processor.provider} className="table-row">
                          <td
                            className="enable"
                            onClick={() => {
                              setModalToggleProcessorStatusModal(true);
                              setPPDetails(processor);
                            }}
                          >
                            <Switch value={processor.status} onChange={() => {}} />
                          </td>
                          <td className="processorName">{processor.provider}</td>
                          <td className="beneficiaryBank" title={processor.beneficiaryBanks.join(', ')}>
                            <div
                              className="beneficiaryBank__container"
                              onClick={() => {
                                if (processor.beneficiaryBanks.length > 4) {
                                  setViewProcessorBanksModal(true);
                                  setPPDetails(processor);
                                }
                              }}
                              style={{ cursor: processor.beneficiaryBanks.length > 4 ? 'pointer' : 'default' }}
                            >
                              {processor.beneficiaryBanks.length > 0 ? (
                                <>
                                  {processor.beneficiaryBanks.map((bank: any, index: number) => (
                                    <span key={bank.value}>
                                      {bank}
                                      {index < processor.beneficiaryBanks.length - 1 && ', '}
                                    </span>
                                  ))}
                                </>
                              ) : (
                                'N/A'
                              )}
                            </div>
                          </td>
                          <td className="currentBalance">{processor.currentBalance}</td>

                          <td className="minimumBalanceThrehold">{processor.minimumBalanceAlertThreshold}</td>
                          <td className="lastUpdated">{processor.lastEnabledAt}</td>

                          <td className="action">
                            <div className="dropdown-container" ref={actionRef}>
                              <button onClick={(e) => handleShowDropdown(e, i)} className="pointer">
                                <LuMoreVertical color="#CBD5E1" size={25} />
                              </button>

                              {showDropdown.index === i && showDropdown.show && (
                                <div className="table-dropdown" ref={dropdownRef}>
                                  <Dropdown data={dropdownArray} index={i} />
                                </div>
                              )}
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          </>
        )}
      </div>

      <ModalUpdateMinimumBalanceThreshold
        modalIsOpen={minimumBalanceModal}
        setIsOpen={setMinimumBalanceModal}
        data={ppDetails}
        action={() => setMinimumBalanceModal(false)}
      />

      <ModalSwitchProcessorBeneficiaryBank
        modalIsOpen={processorBanksModal}
        setIsOpen={setProcessorBanksModal}
        data={ppDetails}
        action={() => setProcessorBanksModal(false)}
      />

      <ModalToggleProcessorStatus
        modalIsOpen={ModalToggleProcessorStatusModal}
        setIsOpen={setModalToggleProcessorStatusModal}
        data={ppDetails}
        action={() => setModalToggleProcessorStatusModal(false)}
      />

      <ModalViewProcessorBanks
        modalIsOpen={viewProcessorBanksModal}
        setIsOpen={setViewProcessorBanksModal}
        data={ppDetails}
        action={() => setViewProcessorBanksModal(false)}
      />
    </>
  );
};

export default PaymentProcessor;
